import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface Milestone {
  year: string;
  title: string;
  description: string;
  icon: string;
}

export default function CertificationTimeline() {
  const [hoveredMilestone, setHoveredMilestone] = useState<number | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [editingField, setEditingField] = useState<{ index: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Load milestones from localStorage or use defaults
  useEffect(() => {
    const saved = localStorage.getItem('milestones');
    if (saved) {
      setMilestones(JSON.parse(saved));
    } else {
      const defaults: Milestone[] = [
        {
          year: '2008',
          title: 'Company Founded',
          description: 'Click to edit description',
          icon: 'ri-flag-line',
        },
        {
          year: '2010',
          title: 'DOT Certification',
          description: 'Click to edit description',
          icon: 'ri-award-line',
        },
        {
          year: '2012',
          title: 'Fleet Expansion',
          description: 'Click to edit description',
          icon: 'ri-truck-line',
        },
        {
          year: '2015',
          title: 'West Coast Coverage',
          description: 'Click to edit description',
          icon: 'ri-map-pin-range-line',
        },
        {
          year: '2018',
          title: 'Safety Excellence Award',
          description: 'Click to edit description',
          icon: 'ri-shield-star-line',
        },
        {
          year: '2020',
          title: 'Technology Integration',
          description: 'Click to edit description',
          icon: 'ri-smartphone-line',
        },
        {
          year: '2022',
          title: 'Green Initiative',
          description: 'Click to edit description',
          icon: 'ri-leaf-line',
        },
        {
          year: '2024',
          title: 'LONE TRS LLC',
          description: 'Click to edit description',
          icon: 'ri-rocket-line',
        },
      ];
      setMilestones(defaults);
      localStorage.setItem('milestones', JSON.stringify(defaults));
    }
  }, []);

  const saveMilestones = (updated: Milestone[]) => {
    setMilestones(updated);
    localStorage.setItem('milestones', JSON.stringify(updated));
  };

  const startEdit = (index: number, field: string, currentValue: string) => {
    setEditingField({ index, field });
    setEditValue(currentValue);
  };

  const saveEdit = () => {
    if (editingField === null) return;
    
    const updated = [...milestones];
    updated[editingField.index] = {
      ...updated[editingField.index],
      [editingField.field]: editValue,
    };
    
    saveMilestones(updated);
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const resetToDefaults = () => {
    if (confirm('Reset all timeline milestones to default values?')) {
      localStorage.removeItem('milestones');
      window.location.reload();
    }
  };

  const renderEditableField = (index: number, field: string, value: string, className: string = '') => {
    const isEditing = editingField?.index === index && editingField?.field === field;
    
    if (isEditing) {
      return (
        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          {field === 'description' ? (
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
              className="bg-white/20 border border-electric-blue text-white px-2 py-1 rounded text-sm flex-1 min-h-[60px]"
              autoFocus
            />
          ) : (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
              className="bg-white/20 border border-electric-blue text-white px-2 py-1 rounded text-sm flex-1"
              autoFocus
            />
          )}
          <button onClick={saveEdit} className="text-green-400 hover:text-green-300 cursor-pointer">
            <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
          </button>
          <button onClick={cancelEdit} className="text-red-400 hover:text-red-300 cursor-pointer">
            <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      );
    }
    
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          startEdit(index, field, value);
        }}
        className={`${className} cursor-pointer hover:bg-white/5 rounded px-2 py-1 transition-all duration-200 group relative`}
      >
        {value}
        <i className="ri-pencil-line absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-electric-blue text-xs w-4 h-4 flex items-center justify-center"></i>
      </div>
    );
  };

  return (
    <section className="py-24 bg-dark-800 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            OUR <span className="text-electric-blue">JOURNEY</span>
          </h2>
          <p className="font-inter text-white/60 text-lg mb-4">
            Company milestones and achievements
          </p>
          <button
            onClick={resetToDefaults}
            className="text-white/40 hover:text-electric-blue text-sm font-inter transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-refresh-line mr-2 w-4 h-4 inline-flex items-center justify-center"></i>
            Reset to Defaults
          </button>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-electric-blue via-electric-cyan to-electric-blue" />

          {/* Milestones */}
          <div className="space-y-16">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                onMouseEnter={() => setHoveredMilestone(index)}
                onMouseLeave={() => setHoveredMilestone(null)}
                className={`flex items-center ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-12' : 'text-left pl-12'}`}>
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:bg-white/15 hover:border-electric-blue/50 transition-all duration-300 hover:shadow-xl hover:shadow-electric-blue/20">
                    <div className="font-orbitron text-electric-blue text-sm tracking-wider mb-2">
                      {renderEditableField(index, 'year', milestone.year, 'font-orbitron text-electric-blue text-sm tracking-wider')}
                    </div>
                    <h3 className="font-orbitron text-xl font-bold text-white mb-2">
                      {renderEditableField(index, 'title', milestone.title, 'font-orbitron text-xl font-bold text-white')}
                    </h3>
                    <div className="font-inter text-white/70 text-sm leading-relaxed">
                      {renderEditableField(index, 'description', milestone.description, 'font-inter text-white/70 text-sm leading-relaxed')}
                    </div>
                  </div>
                </div>

                {/* Center Node */}
                <div className="relative z-10 flex items-center justify-center w-2/12">
                  <motion.div
                    animate={{
                      scale: hoveredMilestone === index ? 1.2 : 1,
                      boxShadow: hoveredMilestone === index 
                        ? '0 0 30px rgba(0, 212, 255, 0.8)' 
                        : '0 0 20px rgba(0, 212, 255, 0.5)',
                    }}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-electric-blue to-electric-cyan border-4 border-dark-800 flex items-center justify-center"
                  >
                    <i className={`${milestone.icon} text-2xl text-dark-900 w-6 h-6 flex items-center justify-center`}></i>
                  </motion.div>
                </div>

                {/* Empty Space */}
                <div className="w-5/12" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
